import { useState } from 'react';
import { Alert, Button, Space, App } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useOrganization } from '@/hooks/useOrganization';
import { subscriptionApi } from '@/api/subscription';
import { friendlyError } from '@/lib/errorMessage';

export function PastDueBanner() {
  const { t } = useTranslation();
  const { entitlements } = useEntitlements();
  const { currentOrgId, currentRole } = useOrganization();
  const { message } = App.useApp();
  const [dismissed, setDismissed] = useState(false);
  const [portalLoading, setPortalLoading] = useState<'full' | 'card' | null>(null);

  if (dismissed) return null;
  const status = entitlements?.status;
  if (status !== 'past_due' && status !== 'paused') return null;

  const isPaused = status === 'paused';
  const openPortal = async (flow?: 'payment_method_update') => {
    if (!currentOrgId || portalLoading) return;
    setPortalLoading(flow === 'payment_method_update' ? 'card' : 'full');
    try {
      const { url } = await subscriptionApi.createPortalSession(
        currentOrgId,
        flow ? { flow } : undefined
      );
      if (url) window.location.href = url;
      else void message.error(t('banners.pastDue.portalFailed'));
    } catch (err) {
      void message.error(friendlyError(err, t, 'banners.pastDue.portalFailed'));
    } finally {
      setPortalLoading(null);
    }
  };

  const title = isPaused ? t('banners.pastDue.titlePaused') : t('banners.pastDue.titlePastDue');
  const description = isPaused ? t('banners.pastDue.descPaused') : t('banners.pastDue.descPastDue');

  const canManageBilling = currentRole === 'owner';

  return (
    <Alert
      type={isPaused ? 'info' : 'warning'}
      title={title}
      description={
        <Space orientation="vertical" size={8} style={{ width: '100%' }}>
          <span>{description}</span>
          {canManageBilling && (
            <Space wrap>
              {!isPaused && (
                <Button
                  type="primary"
                  size="small"
                  loading={portalLoading === 'card'}
                  disabled={portalLoading !== null && portalLoading !== 'card'}
                  onClick={() => {
                    void openPortal('payment_method_update');
                  }}
                >
                  {t('banners.pastDue.updatePayment')}
                </Button>
              )}
              <Button
                type={isPaused ? 'primary' : 'default'}
                size="small"
                loading={portalLoading === 'full'}
                disabled={portalLoading !== null && portalLoading !== 'full'}
                onClick={() => {
                  void openPortal();
                }}
              >
                {t('banners.pastDue.manageBilling')}
              </Button>
              <Button type="link" size="small" href="mailto:enterprise@sspwallet.com">
                {t('banners.pastDue.contactSupport')}
              </Button>
            </Space>
          )}
        </Space>
      }
      showIcon
      closable={{
        'aria-label': t('banners.pastDue.dismiss'),
        onClose: () => {
          setDismissed(true);
        },
      }}
      style={{ marginBottom: 16 }}
    />
  );
}

export default PastDueBanner;
